// In-game modal dialogs (alert / confirm / prompt) that match the medieval
// iron/parchment theme. Replaces window.alert/confirm/prompt which are ugly,
// blocked by some embeds, and break the canvas frame.
//
// Each function returns a Promise. Modals render at depth THEME.depths.tooltip
// in their own container so they tear down cleanly without leaking listeners.

import Phaser from "phaser";
import { GAME_HEIGHT, GAME_WIDTH } from "../config";
import { THEME, button, panel } from "./theme";

const MODAL_W = 460;
const PADDING = 22;

export function showAlert(scene: Phaser.Scene, title: string, body = ""): Promise<void> {
  return new Promise(resolve => {
    const m = openShell(scene, title, body, /*hasInput*/ false);
    const btn = button(scene, GAME_WIDTH / 2, m.bottomY, 120, 36, "OK", {
      display: true, fontSize: 14,
      onClick: () => { m.close(); btn.destroy(); resolve(); }
    });
    btn.setDepth(m.depth);
  });
}

export function showConfirm(
  scene: Phaser.Scene, title: string, body = ""
): Promise<boolean> {
  return new Promise(resolve => {
    const m = openShell(scene, title, body, /*hasInput*/ false);
    const cancel = button(scene, GAME_WIDTH / 2 - 75, m.bottomY, 130, 36, "CANCEL", {
      display: true, fontSize: 14, color: THEME.colors.parchmentDim,
      onClick: () => { m.close(); cancel.destroy(); ok.destroy(); resolve(false); }
    });
    const ok = button(scene, GAME_WIDTH / 2 + 75, m.bottomY, 130, 36, "CONFIRM", {
      display: true, fontSize: 14,
      onClick: () => { m.close(); cancel.destroy(); ok.destroy(); resolve(true); }
    });
    cancel.setDepth(m.depth);
    ok.setDepth(m.depth);
  });
}

export function showPrompt(
  scene: Phaser.Scene, title: string, body = "", initial = ""
): Promise<string | null> {
  return new Promise(resolve => {
    const m = openShell(scene, title, body, /*hasInput*/ true);

    // DOM input layered on top of the Phaser canvas. Phaser's text renderer
    // doesn't own focus or IME, so reusing the browser input is the simplest
    // robust path.
    const input = document.createElement("input");
    input.type = "text";
    input.value = initial;
    input.maxLength = 64;
    input.spellcheck = false;
    input.autocomplete = "off";
    Object.assign(input.style, {
      position:    "absolute",
      width:       `${MODAL_W - PADDING * 2}px`,
      padding:     "8px 10px",
      fontFamily:  "'IM Fell English', 'Trebuchet MS', serif",
      fontSize:    "16px",
      color:       "#1a1108",
      background:  "#f2e2b8",
      border:      "1px solid #8a6a3a",
      borderRadius:"4px",
      outline:     "none",
      zIndex:      "9999",
      boxSizing:   "border-box"
    } as Partial<CSSStyleDeclaration>);
    document.body.appendChild(input);

    const reposition = () => {
      const rect = scene.game.canvas.getBoundingClientRect();
      const sx = rect.width  / GAME_WIDTH;
      const sy = rect.height / GAME_HEIGHT;
      input.style.left   = `${rect.left + (GAME_WIDTH / 2 - (MODAL_W - PADDING * 2) / 2) * sx}px`;
      input.style.top    = `${rect.top  + m.inputY * sy}px`;
      input.style.transform = `scale(${sx}, ${sy})`;
      input.style.transformOrigin = "top left";
    };
    reposition();
    window.addEventListener("resize", reposition);

    const finish = (val: string | null) => {
      window.removeEventListener("resize", reposition);
      input.remove();
      m.close();
      cancelBtn.destroy();
      okBtn.destroy();
      resolve(val);
    };

    input.addEventListener("keydown", ev => {
      if (ev.key === "Enter")  { ev.preventDefault(); finish(input.value); }
      if (ev.key === "Escape") { ev.preventDefault(); finish(null); }
    });

    const cancelBtn = button(scene, GAME_WIDTH / 2 - 75, m.bottomY, 130, 36, "CANCEL", {
      display: true, fontSize: 14, color: THEME.colors.parchmentDim,
      onClick: () => finish(null)
    });
    const okBtn = button(scene, GAME_WIDTH / 2 + 75, m.bottomY, 130, 36, "OK", {
      display: true, fontSize: 14,
      onClick: () => finish(input.value)
    });
    cancelBtn.setDepth(m.depth);
    okBtn.setDepth(m.depth);

    setTimeout(() => { input.focus(); input.select(); }, 0);
  });
}

interface ShellHandles {
  close:    () => void;
  bottomY:  number;
  inputY:   number;
  depth:    number;
}

function openShell(
  scene: Phaser.Scene, title: string, body: string, hasInput: boolean
): ShellHandles {
  const baseDepth = THEME.depths.tooltip;

  const lines    = body ? body.split("\n").length : 0;
  const bodyH    = lines * 22;
  const titleH   = 36;
  const inputH   = hasInput ? 56 : 0;
  const buttonsH = 60;
  const h = Math.min(360, Math.max(160, titleH + bodyH + inputH + buttonsH + PADDING));
  const x = (GAME_WIDTH - MODAL_W) / 2;
  const y = (GAME_HEIGHT - h) / 2;

  const blocker = scene.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.55)
    .setOrigin(0).setDepth(baseDepth).setInteractive();

  const bg = panel(scene, x, y, MODAL_W, h, "parchment").setDepth(baseDepth + 1);

  const titleText = scene.add.text(x + MODAL_W / 2, y + 14, title, {
    fontFamily: THEME.fonts.display, fontSize: "20px", color: THEME.colors.iron
  }).setOrigin(0.5, 0).setDepth(baseDepth + 2);

  let bodyText: Phaser.GameObjects.Text | null = null;
  if (body) {
    bodyText = scene.add.text(x + MODAL_W / 2, y + 56, body, {
      fontFamily: THEME.fonts.body, fontSize: "14px", color: THEME.colors.iron,
      align: "center", wordWrap: { width: MODAL_W - PADDING * 2 }
    }).setOrigin(0.5, 0).setDepth(baseDepth + 2);
  }

  return {
    bottomY: y + h - 28,
    inputY:  y + titleH + bodyH + PADDING,
    depth:   baseDepth + 2,
    close: () => {
      blocker.destroy();
      bg.destroy();
      titleText.destroy();
      bodyText?.destroy();
    }
  };
}
